// Con extensión, a diferencia del resto de la web: este módulo lo importan
// también las pruebas, que se compilan con `NodeNext` y la exigen. Vite la
// resuelve igual. Es lo mismo que hace `retroalimentacion.ts`.
import type { MotivoDeFalloDeVoz } from './api.js';

/**
 * Las decisiones de grabar, separadas de la pantalla que graba.
 *
 * Están aquí y no dentro del componente porque son las que hay que poder probar:
 * qué formato se le pide al navegador y qué se le dice a alguien cuando algo
 * falla. Dentro del JSX no se pueden mirar sin montar un navegador entero.
 *
 * NI UNA REFERENCIA AL NAVEGADOR EN ESTE ARCHIVO, y no es purismo: las pruebas
 * lo compilan con la configuración del servidor, donde `MediaRecorder` y
 * `navigator` no existen. Quien sabe preguntarle cosas al navegador es `Voz.tsx`
 * y le pasa la respuesta a `formatoParaGrabar`.
 */

/**
 * Formatos que el servidor sabe transcribir, en orden de preferencia.
 *
 * NO SE ELIGE, SE PREGUNTA. Cada navegador graba en lo suyo y no acepta que le
 * impongan otra cosa: Chrome y Android dan WebM con opus dentro, Safari en
 * iPhone da MP4, Firefox da OGG. Se prueba de arriba abajo y se usa el primero
 * que ese navegador admita.
 */
const FORMATOS = ['audio/webm', 'audio/ogg', 'audio/mp4'] as const;

/**
 * Qué formato usar, o `null` si el navegador no admite ninguno servible.
 *
 * `admite` es lo que este navegador contesta sobre cada formato. Entra como
 * argumento en vez de preguntarse aquí para que se pueda probar el caso del
 * iPhone desde un ordenador, que si no solo se descubre con un iPhone en la
 * mano.
 */
export function formatoParaGrabar(admite: (tipo: string) => boolean): string | null {
  return FORMATOS.find((formato) => admite(formato)) ?? null;
}

/**
 * Cuánto se deja grabar de una vez.
 *
 * No es un límite de producto sino del servicio que transcribe, que tiene su
 * propio tope de tiempo de proceso. Cinco minutos caben de sobra para contar por
 * qué se está aquí, y cortar solo —avisando antes— es mejor que dejar grabar
 * diez y que se pierda entero al mandarlo.
 */
export const MAXIMO_SEGUNDOS = 300;

/** A partir de cuándo se avisa de que queda poco. */
export const AVISO_SEGUNDOS = 270;

/** El tiempo grabado, como se lee en un reloj. */
export function comoReloj(segundos: number): string {
  const minutos = Math.floor(segundos / 60);
  const resto = segundos % 60;
  return `${String(minutos)}:${String(resto).padStart(2, '0')}`;
}

/**
 * Qué se le dice a la persona cuando la grabación no sirvió.
 *
 * NINGUNO DICE «ERROR» y todos terminan en algo que ella puede hacer. Está
 * contando por qué está en terapia: un mensaje que solo informa de que el
 * sistema falló la deja parada en el peor momento posible. Y siempre se le
 * recuerda que puede escribirlo, porque esa salida no desaparece nunca.
 */
export function mensajeDeFalloDeVoz(motivo: MotivoDeFalloDeVoz | 'sin_permiso' | 'red'): string {
  if (motivo === 'sin_permiso') {
    return 'No se pudo usar el micrófono. Puedes darle permiso desde los ajustes del navegador, o escribir tu respuesta aquí abajo.';
  }
  if (motivo === 'red') {
    return 'No llegó la grabación. Revisa la conexión y vuelve a intentarlo, o escribe tu respuesta aquí abajo.';
  }
  if (motivo === 'no_se_entendio') {
    return 'No se oyó nada en la grabación. Vuelve a intentarlo hablando más cerca, o escríbelo aquí abajo.';
  }
  if (motivo === 'demasiado_largo' || motivo === 'demasiado_grande') {
    return 'La grabación quedó muy larga. Prueba a contarlo en un par de minutos, o escríbelo aquí abajo.';
  }
  if (motivo === 'formato') {
    return 'Este navegador grabó en un formato que no se pudo leer. Escribe tu respuesta aquí abajo.';
  }
  return 'No se pudo pasar la grabación a texto. Vuelve a intentarlo en un momento, o escríbelo aquí abajo.';
}
